import { useState, useCallback } from 'react';
import { api } from 'services';
import { Attachment } from './types';

type Uploader<T extends File | FileList, E> = {
  isLoading: boolean;
  error: undefined | E;
  upload: (f: T) => Promise<T extends FileList ? Attachment[] : Attachment>;
};

type UseAttachmentUploaderProps<T extends File | FileList, E> = {
  onSuccess?: (f: T extends FileList ? Attachment[] : Attachment) => void;
  onError?: (e: E) => void;
  /** @deprecated should happen server side  */
  discriminator?: string;
};

function useAttachmentUploader<T extends File | FileList = File, E = unknown>(
  props: UseAttachmentUploaderProps<T, E>,
): Uploader<T, E> {
  const { onSuccess, onError, discriminator } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<undefined | E>(undefined);

  const upload = useCallback(
    async (input: T): Promise<T extends FileList ? Attachment[] : Attachment> => {
      setIsLoading(true);
      setError(undefined);

      const files = input instanceof FileList ? Array.from(input) : [input as File];

      try {
        const uploadPromises = files.map(async (file) => {
          const response = await api.put<Attachment, unknown>(
            `attachments/attachments${discriminator ? `/${discriminator}/` : '/'}`,
            file,
            {
              headers: {
                'Content-Disposition': `attachment; filename*="${encodeURIComponent(file.name)}"`,
                'Content-Type': file.type,
              },
            },
          );

          if (response.ok) {
            return response.data as Attachment;
          } else {
            throw response.error;
          }
        });

        const attachments = await Promise.all(uploadPromises);
        const result = (input instanceof FileList ? attachments : attachments[0]) as T extends FileList
          ? Attachment[]
          : Attachment;
        onSuccess?.(result);
        return result;
      } catch (err) {
        const errorData = err as E;
        setError(errorData);
        onError?.(errorData);
        throw errorData;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, onError, discriminator],
  );

  return {
    isLoading,
    error,
    upload,
  };
}

export default useAttachmentUploader;
